import React, { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClock, faCopy } from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { TIMESTAMP_ABOUT } from '../content/toolGuides';
import { TIMESTAMP_ROUTE } from '../content/tools';
import {
  EpochUnit,
  parseInstant,
  renderInstant,
  TimestampError,
} from '../utils/timestamp';
import './TimestampConverter.css';

const UNIT_OPTIONS: Array<{ value: '' | EpochUnit; label: string }> = [
  { value: '', label: 'Detect automatically' },
  { value: 'seconds', label: 'Seconds' },
  { value: 'milliseconds', label: 'Milliseconds' },
  { value: 'microseconds', label: 'Microseconds' },
];

const TimestampConverter: React.FC = () => {
  const [input, setInput] = useState('');
  const [unit, setUnit] = useState<'' | EpochUnit>('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { rows, interpretation, error } = useMemo(() => {
    if (!input.trim()) {
      return { rows: null, interpretation: null, error: null };
    }
    try {
      const parsed = parseInstant(input, unit || undefined);
      return {
        rows: renderInstant(parsed.date),
        interpretation: parsed.interpretation,
        error: null,
      };
    } catch (err) {
      return {
        rows: null,
        interpretation: null,
        error: err instanceof TimestampError ? err.message : 'Could not read that value.',
      };
    }
  }, [input, unit]);

  const copy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <>
      <RouteSeo route={TIMESTAMP_ROUTE} />
      <ToolLayout
        title="Unix Timestamp Converter"
        description="Convert epoch seconds or milliseconds to a readable date, and back"
        icon={faClock}
        controls={
          <>
            <div className="tool-controls__group">
              <Form.Group className="timestamp-unit">
                <Form.Label htmlFor="timestamp-unit" className="mb-0 me-2">
                  Read numbers as
                </Form.Label>
                <Form.Select
                  id="timestamp-unit"
                  size="sm"
                  value={unit}
                  onChange={(event) => setUnit(event.target.value as '' | EpochUnit)}
                >
                  {UNIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="tool-controls__group">
              <Button
                variant="link"
                size="sm"
                className="px-0"
                onClick={() => setInput(String(Math.floor(Date.now() / 1000)))}
              >
                Now
              </Button>
              <Button variant="link" size="sm" className="px-0" onClick={() => setInput('1516239022')}>
                Load Example
              </Button>
            </div>
          </>
        }
        error={error}
        inputLabel="Timestamp or date"
        inputValue={input}
        onInputChange={setInput}
        inputPlaceholder={'1516239022\nor 2018-01-18T01:30:22Z'}
        inputRows={8}
        inputHint="Epoch seconds, milliseconds or microseconds, or any date string your browser can parse."
        outputContent={
          rows ? (
            <>
              <h3 className="timestamp-result__heading">Converted</h3>
              <dl className="timestamp-result">
                {rows.map((row) => (
                  <div className="timestamp-result__row" key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>
                      <span className="timestamp-result__value">{row.value}</span>
                      {row.copyable && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => copy(row.value, row.label)}
                          aria-label={`Copy ${row.label}`}
                        >
                          <FontAwesomeIcon icon={copiedField === row.label ? faCheck : faCopy} />
                        </Button>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
              {interpretation && <p className="timestamp-result__note mb-0">{interpretation}</p>}
            </>
          ) : (
            <p className="text-muted mb-0">
              Paste an epoch value or a date and every rendering appears here.
            </p>
          )
        }
        aboutTitle="About Unix Time"
        about={TIMESTAMP_ABOUT}
      >
        <ToolContent route={TIMESTAMP_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default TimestampConverter;
